import {useCarriers} from '../CarrierProvider';
import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {CubeCarrier, FirstMileField, Sender} from '../types';
import {parsePhoneNumberFromString} from 'libphonenumber-js';
import {CarrierViewHeader} from '../CarrierViewHeader';
import {
    classNames,
    createEmailKeyboardLayout,
    Keyboard,
    layout,
    NameKeyboardLayoutDE,
    NameKeyboardLayoutEN,
    PhoneKeyboardLayout,
    PushButton,
    TextInput,
    theme,
    useTypeRef,
    View,
    ViewContent,
    ViewFooter,
    ViewFooterItem
} from '@variocube/cube-ui';
import {Language} from '@variocube/app-api';
import {stylesheet} from 'typestyle';
import {useLocalization} from "../../i18n";

interface SenderViewProps {
    sender?: Sender;
    carrierId: string;
    onBack: () => void;
    onSubmit: (customer: Sender) => void;
}

enum CustomerField {
    name,
    email,
    phone
}

const emailKeyboardLayout = createEmailKeyboardLayout(true);

function CustomerDataInputView({sender, carrierId, onBack, onSubmit}: SenderViewProps) {
    const {t, language} = useLocalization();
    const carriers = useCarriers();
    const [name, setName] = useState("");
    const [nameWasTyped, setNameWasTyped] = useState<boolean>(false);
    const [email, setEmail] = useState("");
    const [emailWasTyped, setEmailWasTyped] = useState<boolean>(false);
    const [phone, setPhone] = useState("");
    const [phoneWasTyped, setPhoneWasTyped] = useState<boolean>(false);
    const [nameType, setNameType] = useTypeRef();
    const [emailType, setEmailType] = useTypeRef();
    const [phoneType, setPhoneType] = useTypeRef();
    const [field, setField] = useState(CustomerField.name);
    const nameRef = useRef<HTMLInputElement>();
    const emailRef = useRef<HTMLInputElement>();
    const [confirm, setConfirm] = useState<boolean>(false);

    useEffect(() => {
        if (sender) {
            setName(sender.name);
            setPhone(sender.phone ?? "");
            setEmail(sender.email ?? "");
        }
    }, [sender]);

    const carrier = useMemo<CubeCarrier|undefined>(() => {
        if(carriers) {
            return carriers[carrierId];
        }
    }, [carriers, carrierId]);

    const handleSubmit = useCallback(() => {
        onSubmit({
            name: name,
            email: email,
            phone: phone
        });
    }, [onSubmit, name, email, phone]);

    const handleConfirm = useCallback(() => {
        if(carrier && carrier.firstMile && carrier.firstMile.verifyContactData) {
            setConfirm(true);
        }else {
            handleSubmit();
        }
    }, [carrier, handleSubmit]);

    const handleBack = useCallback(() => {
        if(confirm) {
            setConfirm(false);
        } else {
            onBack();
        }
    }, [confirm, onBack]);

    const showField = useCallback((field: FirstMileField) => {
        if(carrier && carrier.firstMile) {
            return carrier.firstMile.availableFields.indexOf(field) != -1;
        }
        return true;
    }, [carrier]);

    const fieldRequired = useCallback((field: FirstMileField) => {
        if(carrier && carrier.firstMile) {
            return carrier.firstMile.requiredFields.indexOf(field) != -1;
        }
        return true;
    }, [carrier]);

    const nameValid = useMemo<boolean>(() => {
        if (fieldRequired(FirstMileField.Name) && name.length <= 2) {
            return false;
        }
        return true;
    }, [carrier, name]);

    const emailValid = useMemo<boolean>(() => {
        const allContactFieldsRequired = Boolean(carrier && carrier.firstMile && carrier.firstMile.allContactFieldsRequired);
        let emailValid = !fieldRequired(FirstMileField.Email) || isValidEmail(email);
        if(allContactFieldsRequired) {
            return emailValid;
        }else {
            const parsedPhoneNumber = (phone && (phone.startsWith("0") || phone.startsWith("+"))) ? parsePhoneNumberFromString(phone, "AT") : undefined;
            let phoneValid = !fieldRequired(FirstMileField.Phone) || (parsedPhoneNumber && parsedPhoneNumber.isValid());
            return phoneValid || emailValid;
        }
    }, [carrier, email, phone]);

    const phoneValid = useMemo<boolean>(() => {
        const allContactFieldsRequired = Boolean(carrier && carrier.firstMile && carrier.firstMile.allContactFieldsRequired);
        const parsedPhoneNumber = (phone && (phone.startsWith("0") || phone.startsWith("+"))) ? parsePhoneNumberFromString(phone, "AT") : undefined;
        let phoneValid = !fieldRequired(FirstMileField.Phone) || (parsedPhoneNumber && parsedPhoneNumber.isValid());
        if(allContactFieldsRequired) {
            return Boolean(phoneValid);
        }else {
            let emailValid = !fieldRequired(FirstMileField.Email) || isValidEmail(email);
            return phoneValid || emailValid;
        }
    }, [carrier, email, phone]);

    const valid = useMemo(() => {
        return nameValid && emailValid && phoneValid;
    }, [nameValid, emailValid, phoneValid]);

    const handleNameFocus = useCallback(() => setField(CustomerField.name), []);
    const handleNameChanged = useCallback((name: string) => {
        setName(name);
        setNameWasTyped(true);
    }, []);

    const handleEmailFocus = useCallback(() => setField(CustomerField.email), []);
    const handleEmailChanged = useCallback((email: string) => {
        setEmail(email);
        setEmailWasTyped(true);
    }, []);

    const handlePhoneFocus = useCallback(() => setField(CustomerField.phone), []);
    const handlePhoneChanged = useCallback((phone: string) => {
        setPhone(phone);
        setPhoneWasTyped(true);
    }, []);


    useEffect(() => {
        nameRef.current?.focus();
    }, []);

    const keyboardLayout = language == Language.DE ? NameKeyboardLayoutDE : NameKeyboardLayoutEN;

    return (
        <View>
            <CarrierViewHeader
                onBack={handleBack}
                title={t("Sender")}
                description={confirm ? t("SenderConfirm") : t("SenderDescription")}
            />
            <ViewContent className={layout.paddingSmall}>
                {!confirm &&
                    <>
                      <div className={layout.horizontal}>
                          {showField(FirstMileField.Name) &&
                              <div className={styles.field}>
                                <div className={styles.label}>
                                    {t("Name")}
                                </div>
                                <TextInput
                                    value={name}
                                    error={nameWasTyped && !nameValid}
                                    onChange={handleNameChanged}
                                    onFocus={handleNameFocus}
                                    inputRef={nameRef}
                                    typeRef={setNameType}
                                    outline
                                    backspace={false}
                                />
                              </div>
                          }
                          {showField(FirstMileField.Phone) &&
                              <div className={styles.field}>
                                <div className={styles.label}>
                                    {t("Phone")}
                                </div>
                                <TextInput
                                    value={phone}
                                    error={phoneWasTyped && !phoneValid}
                                    onChange={handlePhoneChanged}
                                    onFocus={handlePhoneFocus}
                                    typeRef={setPhoneType}
                                    outline
                                    backspace={false}
                                />
                              </div>
                          }
                      </div>
                      <div className={classNames(layout.horizontal, layout.alignEnd)}>
                          {showField(FirstMileField.Email) &&
                              <div className={classNames(layout.fill, styles.field)}>
                                <div className={styles.label}>
                                    {t("Email")}
                                </div>
                                <TextInput
                                    value={email}
                                    error={emailWasTyped && !emailValid}
                                    onChange={handleEmailChanged}
                                    onFocus={handleEmailFocus}
                                    typeRef={setEmailType}
                                    inputRef={emailRef}
                                    outline
                                    backspace={false}
                                />
                              </div>
                          }
                        <div className={styles.field}>
                          <PushButton primary className={styles.submit} disabled={!valid} onClick={handleConfirm}>
                              {t("Next")}
                          </PushButton>
                        </div>
                      </div>
                    </>
                }
                {confirm &&
                    <div className={classNames(layout.vertical, layout.center, layout.alignCenter, styles.confirmItem)}>
                      <div className={classNames(theme.moreContrast, layout.vertical, layout.spaceBetween)}>
                          {name &&
                              <div className={styles.confirmItem}>
                                <label className={styles.confirmLabel}>{t("Name")}</label>
                                <div className={styles.confirmValue}>{name}</div>
                              </div>
                          }
                          {email &&
                              <div className={styles.confirmItem}>
                                <label className={styles.confirmLabel}>{t("Email")}</label>
                                <div className={styles.confirmValue}>{email}</div>
                              </div>
                          }
                          {phone &&
                              <div className={styles.confirmItem}>
                                <label className={styles.confirmLabel}>{t("Phone")}</label>
                                <div className={styles.confirmValue}>{phone}</div>
                              </div>
                          }
                      </div>
                    </div>
                }
            </ViewContent>
            <ViewFooter className={theme.lessContrast}>
                {!confirm &&
                    <ViewFooterItem fill className={styles.keyboardContainer}>
                        {field == CustomerField.name && (
                            <Keyboard onType={nameType} layout={keyboardLayout}/>
                        )}
                        {field == CustomerField.email && (
                            <Keyboard onType={emailType} layout={emailKeyboardLayout}/>
                        )}
                        {field == CustomerField.phone && (
                            <Keyboard onType={phoneType} layout={PhoneKeyboardLayout}/>
                        )}
                    </ViewFooterItem>
                }
                {confirm &&
                    <>
                      <ViewFooterItem fill className={styles.confirmContainer}>
                        <PushButton onClick={handleBack}>
                            {t("Back")}
                        </PushButton>
                      </ViewFooterItem>
                      <ViewFooterItem className={styles.confirmContainer}>
                        <PushButton primary onClick={handleSubmit}>
                            {t("Confirm")}
                        </PushButton>
                      </ViewFooterItem>
                    </>
                }
            </ViewFooter>
        </View>
    );
}

const styles = stylesheet({
    submit: {
        height: "3em"
    },
    label: {
        paddingLeft: "1.5em"
    },
    field: {
        padding: "0.75rem"
    },
    keyboardContainer: {
        padding: "0.25rem"
    },
    confirmContainer: {
        padding: "0.75rem"
    },
    confirmItem: {
        paddingTop: "1.5rem",
        paddingBottom: "1.5rem"
    },
    confirmLabel: {
        fontSize: "1.25em"
    },
    confirmValue: {
        fontSize: "2em"
    }
})

const EmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(email: string) {
    return EmailRegex.test(email);
}

export default CustomerDataInputView;